<template>
  <div class="order-status-page">
    <div class="order-status-card" v-if="!failed">
      <div class="order-status-icon order-status-icon--success">
        <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
      </div>
      <h2 class="order-status-title">{{$t('orderStatus.successTitle')}}</h2>
      <p class="order-status-desc">{{isGroup ? $t('orderStatus.successGroupDesc') : $t('orderStatus.successDesc')}}</p>
      <div class="order-status-actions">
        <router-link v-if="isGroup" :to="{name: 'group-start'}">
          <button class="btn primary">{{$t('orderStatus.viewGroup')}}</button>
        </router-link>
        <router-link v-else :to="{name: 'order-list'}">
          <button class="btn primary">{{$t('orderStatus.viewOrders')}}</button>
        </router-link>
      </div>
    </div>
    <div class="order-status-card" v-else>
      <div class="order-status-icon order-status-icon--error">
        <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </div>
      <h2 class="order-status-title">{{$t('orderStatus.failedTitle')}}</h2>
      <p class="order-status-desc">{{$t('orderStatus.failedDesc')}}</p>
      <div class="order-status-actions">
        <router-link :to="{name: 'shelf'}">
          <button class="btn default">{{$t('orderStatus.backToShelf')}}</button>
        </router-link>
        <button class="btn primary" v-if="!isGroup"
                @click="$router.push({name: 'order-check', query: {bid: order.bid, binding: order.binding, quantity: order.quantity}})">
          {{$t('orderStatus.retryPayment')}}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import api from 'src/api'
import { mapActions } from 'vuex'
import FBShare from 'src/mixins/FBShare'
import { trackPurchase } from 'src/utils/track'
export default {
  name: 'order-status',
  mixins: [FBShare],
  data () {
    return {
      orderLoaded: false,
      order: {},
      failed: false,
      isGroup: false
    }
  },
  async created () {
    const { failed, type } = this.$route.query
    const orderId = this.$route.query.order_id
    const sessionId = this.$route.query.session_id
    const gid = this.$route.query.group_oid

    if (type === 'stripe' && sessionId && orderId) {
      // Stripe Checkout 回调 — 验证支付状态
      await this.verifyStripePayment(sessionId, orderId)
      return
    }
    if (orderId) {
      await this.fetchOrderStatus(orderId)
      return
    }
    if (gid) {
      await this.fetchGroupOrder(gid)
    }
    this.failed = failed || this.failed
  },
  methods: {
    ...mapActions([
      'setLoading'
    ]),
    fetchGroupOrder (oid) {
      this.isGroup = true
      return api.fetchGroupCheckout(oid).then(data => {
        if (!data || data.errors) {
          const msg = data.errors ? `- ${data.errors.code}` : ''
          this.$toast({
            message: `數據請求錯誤<br>請聯繫客服 錯誤碼 ${msg}`
          })
          return
        }
        this.orderLoaded = true
        if (data.paid) {
          this.fireConversion(data, { groupCoupon: true })
        } else {
          this.failed = true
        }
      })
    },
    async verifyStripePayment (sessionId, orderId) {
      try {
        this.setLoading({ text: 'Verifying payment...', visible: true })
        const data = await api.verifyStripeCheckout(sessionId, orderId)
        this.setLoading(false)
        this.orderLoaded = true
        if (!data || data.errors) {
          this.failed = true
          return
        }
        this.order = data
        if (data.paid) {
          this.fireConversion(data)
        } else {
          this.failed = true
        }
      } catch (e) {
        this.setLoading(false)
        this.failed = true
      }
    },
    fetchOrderStatus (orderId) {
      return api.fetchOrder(orderId).then(data => {
        this.setLoading(false)
        this.orderLoaded = true
        this.order = data
        if (data.paid) {
          this.fireConversion(data)
        }
        return data
      })
    },
    fireConversion (data, opts) {
      // GA4 标准 ecommerce purchase（同时触发 Google Ads conversion，
      // 见 utils/track.js：window.GOOGLE_ADS_CONVERSION_SEND_TO 配了就发）
      // paidMoney 来自后端 verify_session 的 paid_money 字段（axios 拦截器
      // camelCase 后），是 Stripe 实际收款金额（含运费、折扣后），其余三个
      // 字段保留作为其他接口返回值的 fallback。
      const value = Number(
        data.paidMoney || data.totalPrice || data.amount || data.totalCost || 0
      )
      const items = [{
        item_id: String(data.bid || data.orderId),
        item_name: data.bookName || 'iPastBook',
        item_category: data.binding || '',
        quantity: Number(data.quantity || 1),
        price: data.quantity ? Number((value / data.quantity).toFixed(2)) : value
      }]
      trackPurchase({
        orderId: data.orderId,
        value: value,
        currency: data.currency || 'USD',
        items: items,
        coupon: data.couponNo || undefined
      })
      if (opts && opts.groupCoupon) {
        // 仅作 funnel 标记：组购成交
        try { window.gtag && window.gtag('event', 'group_purchase', { transaction_id: String(data.orderId) }) } catch (e) {}
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.order-status-page {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
  padding: 2rem;
}

.order-status-card {
  text-align: center;
  max-width: 400px;
}

.order-status-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;

  &--success { background-color: #68C5B3; }
  &--error { background-color: #F56368; }
}

.order-status-title {
  font-size: 20px;
  font-weight: 700;
  color: #222;
  margin: 0 0 0.5rem;
}

.order-status-desc {
  font-size: 14px;
  color: #888;
  margin: 0 0 2rem;
}

.order-status-actions {
  display: flex;
  justify-content: center;
  gap: 12px;

  .btn {
    min-width: 140px;
    padding: 10px 24px;
    border-radius: 8px;
    font-size: 14px;
  }
}
</style>
